// NewLeadAlert.jsx
// Global popup + chime that fires the moment a new lead lands in the DB.
// Only visible to front-desk / director / admin / owner roles.
//
// What it does:
//   • Subscribes to leads INSERT in realtime
//   • Plays an attention chime (Web Audio — no asset needed)
//   • Shows a full-screen modal with the lead's details
//   • Live counter of seconds since the lead arrived
//   • "Call Now" button (tel: link) writes first_acknowledged_at + seconds-late to DB
//   • Multiple incoming leads stack as a queue
//
// Pair this with an admin dashboard widget that surfaces response-time stats.

import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", border:"#e8e2d8",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6a2a",
  green:"#2d8a55", red:"#b54040", muted:"#6b6356", text:"#1a1a2e", light:"#f4f0ea",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

// Roles allowed to receive new-lead pop-ups
// ★ Profiles store the front desk role as "frontdesk" (no underscore) — with
//   only "front_desk" here the receptionists never got a single popup.
const ALERT_ROLES = new Set(["frontdesk", "front_desk", "director", "admin", "owner", "manager"]);

function formatPhone(p) {
  if (!p) return "";
  const d = p.replace(/\D/g, "");
  if (d.length === 10) return "(" + d.slice(0,3) + ") " + d.slice(3,6) + "-" + d.slice(6);
  if (d.length === 11 && d.startsWith("1")) return "+1 (" + d.slice(1,4) + ") " + d.slice(4,7) + "-" + d.slice(7);
  return p;
}

// Shared AudioContext — most browsers block audio until the user interacts
// with the page. We create ONE context up front and resume it on the first
// click/keypress anywhere in the document. After that, alert chimes play freely.
let _sharedAudioCtx = null;
let _audioUnlocked = false;

function getAudioCtx() {
  if (!_sharedAudioCtx) {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) _sharedAudioCtx = new Ctx();
    } catch (e) { /* ignore */ }
  }
  return _sharedAudioCtx;
}

if (typeof window !== "undefined" && !window.__elyonAudioUnlockBound) {
  window.__elyonAudioUnlockBound = true;
  const unlock = () => {
    const ctx = getAudioCtx();
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
    _audioUnlocked = true;
  };
  ["click", "keydown", "touchstart", "pointerdown"].forEach(ev =>
    window.addEventListener(ev, unlock, { once: false, passive: true })
  );
}

function playOneSweep(ctx, startAt) {
  const notes = [880, 1108, 1318, 1568]; // A5, C#6, E6, G6 — clear, attention-grabbing
  notes.forEach((freq, i) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = freq;
    o.connect(g);
    g.connect(ctx.destination);
    const t0 = startAt + i * 0.14;
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(0.35, t0 + 0.025); // louder than before
    g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.3);
    o.start(t0);
    o.stop(t0 + 0.32);
  });
}

// Web-Audio chime — plays through the shared context. Repeats 3 times so it
// truly cannot be missed. Falls back silently if the browser is hostile.
function playChime() {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
    const base = ctx.currentTime + 0.05;
    playOneSweep(ctx, base);
    playOneSweep(ctx, base + 0.85);
    playOneSweep(ctx, base + 1.7);
  } catch (e) { console.warn("Chime failed:", e); }
}

export default function NewLeadAlert({ user, profile }) {
  const [queue, setQueue] = useState([]); // [{lead, arrivedAt}]
  const [now, setNow] = useState(Date.now());
  const seenIdsRef = useRef(new Set());
  const role = (profile?.role || "").toLowerCase();
  // ★ Respect the user's notification preferences. If they toggled OFF "new
  // leads" in their User settings, this popup must NOT fire. Default ON when
  // the pref isn't set yet (legacy behaviour). Setting it to false in the DB
  // silences both the chime + the modal for THIS user only — other users with
  // it ON still get alerted.
  const notifEnabled = profile?.notif_prefs?.new_leads !== false;
  const isAllowed = ALERT_ROLES.has(role) && notifEnabled;

  // Tick every second to update the "X seconds since lead arrived" counter
  useEffect(() => {
    if (queue.length === 0) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [queue.length]);

  // Subscribe to lead status changes (only for allowed roles).
  //
  // We DON'T pop on every new lead — the bot gets first crack (5-min grace
  // window via the pipeline-auto-progress cron). The popup fires only when
  // pipeline_status transitions to "to_call":
  //   • new → bot_chatting → (5 min no reply) → to_call   ← POPUP
  //   • new → bot_chatting → AI books → booked            ← no popup, all good
  //
  // We also fire on callback time arriving (callback_scheduled → to_call).
  useEffect(() => {
    if (!isAllowed) return;

    // Helper that enqueues a lead and plays the chime
    const surface = (lead, arrivedAt) => {
      if (!lead || !lead.id) return;
      // ★ No popup for the cold-outreach list (owner 2026-10-16). ~100 old
      //   contacts a day move bot_chatting → to_call; their popups buried the
      //   real new leads and the desk ended up calling cold contacts first.
      if (lead.source === "cold_outreach") return;
      if (seenIdsRef.current.has(lead.id)) return;
      seenIdsRef.current.add(lead.id);
      setQueue(q => [...q, { lead, arrivedAt }]);
      playChime();
    };

    // On mount, catch any leads that ALREADY moved to to_call but no one has
    // acknowledged yet — surfaces them so the staff doesn't miss them after
    // refreshing the page.
    (async () => {
      const cutoff = new Date(Date.now() - 30 * 60 * 1000).toISOString(); // last 30 min only
      const { data } = await supabase
        .from("leads")
        .select("*")
        .eq("pipeline_status", "to_call")
        .is("first_acknowledged_at", null)
        .gte("pipeline_status_changed_at", cutoff)
        .order("pipeline_status_changed_at", { ascending: false })
        .limit(5);
      (data || []).forEach(lead => {
        const arrived = lead.pipeline_status_changed_at
          ? new Date(lead.pipeline_status_changed_at).getTime()
          : Date.now();
        surface(lead, arrived);
      });
    })();

    const ch = supabase.channel("new-lead-alert-" + Math.random().toString(36).slice(2))
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "leads" }, (payload) => {
        const lead = payload.new;
        const prev = payload.old || {};
        if (!lead || !lead.id) return;
        // Only fire on the TRANSITION into to_call — not every update of a to_call lead
        if (lead.pipeline_status !== "to_call") return;
        if (prev.pipeline_status === "to_call") return;
        // If a human already acknowledged this one (e.g. from another browser), skip
        if (lead.first_acknowledged_at) return;
        const arrived = lead.pipeline_status_changed_at
          ? new Date(lead.pipeline_status_changed_at).getTime()
          : Date.now();
        surface(lead, arrived);
      })
      // Edge case: an INSERT that's already to_call (e.g. an old lead being re-pushed
      // via webhook with status=to_call). Rare, but safe to surface immediately.
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "leads" }, (payload) => {
        const lead = payload.new;
        if (!lead || lead.pipeline_status !== "to_call") return;
        if (lead.first_acknowledged_at) return;
        const arrived = lead.created_at ? new Date(lead.created_at).getTime() : Date.now();
        surface(lead, arrived);
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [isAllowed]);

  if (!isAllowed || queue.length === 0) return null;

  // Always show the OLDEST lead on top (the one waiting the longest)
  const current = queue[0];
  const lead = current.lead;
  const elapsedSec = Math.max(0, Math.floor((now - current.arrivedAt) / 1000));
  const isLate = elapsedSec > 60;
  const isVeryLate = elapsedSec > 300; // 5 minutes

  const acknowledge = async (action) => {
    try {
      const update = {
        first_acknowledged_at: new Date().toISOString(),
        first_acknowledged_by_user_id: user?.id || null,
        first_acknowledged_by_name: profile?.full_name || profile?.email || "Staff",
        first_acknowledged_seconds: elapsedSec,
        acknowledged_action: action,
      };
      await supabase.from("leads").update(update).eq("id", lead.id);
      await supabase.from("lead_activities").insert({
        lead_id: lead.id,
        activity_type: action === "called" ? "manual_call" : "lead_acknowledged",
        channel: "system",
        direction: "system",
        content: `${profile?.full_name || profile?.email || "Staff"} acknowledged this lead via popup (${action}) — ${elapsedSec}s after arrival`,
      });
    } catch (e) { console.warn("Ack failed:", e); }

    setQueue(q => q.slice(1));
  };

  const dismiss = () => setQueue(q => q.slice(1));
  const snooze = () => {
    setQueue(q => {
      const [head, ...rest] = q;
      // Push to the back so the next one shows, then this comes back later
      return [...rest, head];
    });
  };

  const tel = (lead.phone || "").replace(/[^\d+]/g, "");
  const elapsedDisplay = elapsedSec < 60
    ? `${elapsedSec}s`
    : `${Math.floor(elapsedSec/60)}m ${elapsedSec%60}s`;

  const accent = isVeryLate ? C.red : isLate ? "#e87d1f" : C.green;

  return (
    <div style={{
      position:"fixed",
      inset:0,
      background:"rgba(10,10,24,0.78)",
      zIndex:9999,
      display:"flex",
      alignItems:"center",
      justifyContent:"center",
      padding:20,
      backdropFilter:"blur(6px)",
      animation:"el-fade-in 0.2s ease-out",
    }}>
      <div style={{
        background:"#fff",
        borderRadius:18,
        width:"100%",
        maxWidth:520,
        padding:0,
        boxShadow:"0 25px 80px rgba(0,0,0,0.5), 0 0 0 1px "+C.borderGold,
        overflow:"hidden",
        border:"2px solid "+accent,
      }}>
        {/* Top banner — pulsing depending on lateness */}
        <div style={{
          padding:"14px 22px",
          background:"linear-gradient(135deg, "+accent+", "+accent+"dd)",
          color:"#fff",
          display:"flex",
          justifyContent:"space-between",
          alignItems:"center",
          gap:12,
        }}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{
              width:12,height:12,borderRadius:"50%",background:"#fff",
              boxShadow:"0 0 0 0 rgba(255,255,255,0.8)",
              animation:"el-pulse-gold 1.4s infinite",
            }}/>
            <div style={{fontSize:13,fontWeight:800,letterSpacing:"0.18em",textTransform:"uppercase"}}>
              {lead.needs_human_reply
                ? (isVeryLate ? "🚨 Reply Waiting — Respond Now" : "💬 Interested Reply — Respond")
                : (isVeryLate ? "🚨 URGENT — Lead Waiting" : isLate ? "⚠️ Hurry — Lead Waiting" : "🔔 New Lead — Call Now")}
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{
              padding:"3px 11px",
              background:"rgba(255,255,255,0.18)",
              borderRadius:99,
              fontSize:12,
              fontWeight:800,
              fontVariantNumeric:"tabular-nums",
              letterSpacing:"0.04em",
            }}>{elapsedDisplay} since arrival</div>
            {queue.length > 1 && (
              <div style={{padding:"3px 10px",background:"rgba(0,0,0,0.25)",borderRadius:99,fontSize:11,fontWeight:700}}>
                +{queue.length-1} more
              </div>
            )}
          </div>
        </div>

        {/* Body — lead details */}
        <div style={{padding:"26px 28px 20px"}}>
          <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:22}}>
            <div style={{
              width:60,height:60,borderRadius:"50%",
              background:"linear-gradient(135deg, "+C.goldBg+", "+C.gold+"30)",
              border:"2px solid "+C.borderGold,
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:22,fontWeight:800,color:C.goldD,fontFamily:S,flexShrink:0,
            }}>
              {(lead.full_name || lead.first_name || "?").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:24,fontWeight:800,color:C.text,fontFamily:S,letterSpacing:"-0.01em",lineHeight:1.1}}>
                {lead.full_name || ((lead.first_name||"") + " " + (lead.last_name||"")).trim() || "Unnamed Lead"}
              </div>
              <div style={{fontSize:13,color:C.muted,marginTop:5,fontWeight:600}}>
                {lead.lead_temperature === "hot" && <span style={{color:C.red,marginRight:6}}>🔥 HOT</span>}
                {lead.estimated_value > 0 && <span>${lead.estimated_value} est · </span>}
                {lead.source && <span>via {lead.source}</span>}
              </div>
            </div>
          </div>

          {/* The actual unanswered message — so staff knows exactly what they asked */}
          {lead.needs_human_reply && lead.last_inbound_text && (
            <div style={{padding:"13px 15px",background:"rgba(181,64,64,0.06)",border:"1px solid rgba(181,64,64,0.28)",borderRadius:12,marginBottom:18}}>
              <div style={{fontSize:10,color:C.red,fontWeight:800,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:5}}>💬 They texted — no reply yet</div>
              <div style={{fontSize:15,color:C.text,fontWeight:600,lineHeight:1.45,fontStyle:"italic"}}>"{lead.last_inbound_text}"</div>
            </div>
          )}

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:20}}>
            <div style={{padding:"12px 14px",background:C.light,borderRadius:10,border:"1px solid "+C.border}}>
              <div style={{fontSize:10,color:C.muted,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>📱 Phone</div>
              <div style={{fontSize:15,fontWeight:700,color:C.text}}>{formatPhone(lead.phone) || "—"}</div>
            </div>
            <div style={{padding:"12px 14px",background:C.light,borderRadius:10,border:"1px solid "+C.border}}>
              <div style={{fontSize:10,color:C.muted,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>✨ Interest</div>
              <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>{lead.interest || lead.notes?.slice(0,40) || "General inquiry"}</div>
            </div>
            {lead.email && (
              <div style={{padding:"12px 14px",background:C.light,borderRadius:10,border:"1px solid "+C.border,gridColumn:"1 / -1"}}>
                <div style={{fontSize:10,color:C.muted,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>📧 Email</div>
                <div style={{fontSize:13,color:C.text,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{lead.email}</div>
              </div>
            )}
            {lead.notes && (
              <div style={{padding:"12px 14px",background:C.goldBg,borderRadius:10,border:"1px solid "+C.borderGold,gridColumn:"1 / -1"}}>
                <div style={{fontSize:10,color:C.gold,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>📝 Lead Note</div>
                <div style={{fontSize:13,color:C.text,fontWeight:500,lineHeight:1.45}}>{lead.notes}</div>
              </div>
            )}
          </div>

          {/* Primary CTA — biggest, scariest */}
          {tel ? (
            <a
              href={"tel:" + tel}
              onClick={()=>acknowledge("called")}
              style={{
                display:"block",
                padding:"16px",
                background:"linear-gradient(135deg, "+accent+", "+accent+"cc)",
                color:"#fff",
                textDecoration:"none",
                borderRadius:12,
                fontSize:17,
                fontWeight:800,
                letterSpacing:"0.04em",
                textAlign:"center",
                fontFamily:F,
                boxShadow:"0 8px 24px "+accent+"50",
                marginBottom:10,
              }}
            >
              📞 CALL {formatPhone(lead.phone)} NOW
            </a>
          ) : (
            <button onClick={()=>acknowledge("opened")} style={{width:"100%",padding:"16px",background:C.light,color:C.muted,border:"none",borderRadius:12,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:F,marginBottom:10}}>
              No phone on this lead — open in CRM
            </button>
          )}

          <div style={{display:"flex",gap:8}}>
            <button onClick={snooze} style={{flex:1,padding:"11px",background:"#fff",color:C.muted,border:"1px solid "+C.border,borderRadius:10,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>
              ⏭ Show next
            </button>
            <button onClick={()=>acknowledge("called")} style={{flex:1,padding:"11px",background:C.greenL || "rgba(45,138,85,0.10)",color:C.green,border:"1px solid "+C.green+"40",borderRadius:10,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>
              ✓ I called — close
            </button>
            <button onClick={dismiss} style={{flex:1,padding:"11px",background:"#fff",color:C.red,border:"1px solid "+C.red+"40",borderRadius:10,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}>
              ✕ Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
